/**
 * NOTE: These utils used in wrappers/components
 * located in '@/classWrappers' dir is a implementation of
 * Parent component wrapper pattern (e.g. https://til.hashrocket.com/posts/c5725d4d01-react-wrapper-components-with-nested-children)
 * and HOC pattern to inject dependencies (in this case classes) (more about this technique https://reactjs.org/docs/higher-order-components.html)
 */

// Dependencies
import isObject from 'lodash/isObject';

/**
 * Returns instance's parent store
 * (because functional component doesn't have access to it)
 * @param {vNode} context - vNode functional component instance
 * @return {Object | Boolean} - store object or false value
 */
const getParentStore = (context) => {
    if (context.$store) return context.$store;
    else if (context.parent) return getParentStore(context.parent);
    else if (context.$parent) return getParentStore(context.$parent);
    else return false;
}

/**
 * Returns cssClasses names cached in store
 * @param {vNode} context - vNode functional component instance
 * @return {Object | Boolean} - store object or false value
 */
const getCssClasses = (context) => {
    const store = getParentStore(context);
    return store ? store.state.pokerChipCalcModule.cssClasses : false;
} 

/**
 * Returns vNode's slot (children) data
 * (such as tag name, passed props/data, children inside slot)
 * @param {vNode} vNode  - component instance
 * @param {Array.<String>} newClasses - array of new slot (children) classes
 * @return {Object.<{ tag, data, children }>} - data about slot (children) component
 */
function getSlotElementData (vNode, newClasses) {
    const isComp = !!vNode.componentOptions;
    const tag = isComp ? vNode.componentOptions.Ctor : vNode.tag
    if (!isComp && !tag) return vNode.text

    const children = isComp
        ? (vNode.componentOptions.children || {}) 
        : vNode.children || vNode.text || {};
    const nodeProps = isComp ? { props: vNode.componentOptions.propsData } : {};
    const nodeListeners = isComp ? { on: vNode.componentOptions.listeners } : {}
    const nodeData = vNode.data || {};
    const nodeClasses = nodeData.class ? vNode.data.class : [];

    const classes = Array.from(
        new Set(
            newClasses
                .concat(nodeClasses)
                .filter(Boolean)
        )
    );
    const data = Object.assign(nodeData, nodeProps, nodeListeners, { class: classes });
    return { tag, data, children }
}

/**
 * Returns new instances of slots 
 * (children components) with new classes
 * @param {Function} createEl - Vue create element render function
 * @param {Array.<vNode>} childrens - Array of vNode's
 * @param {Array.<String>} newClasses - array of new slot (children) classes 
 */
const getSlots = (createEl, childrens, newClasses) => {
    return childrens
        .map((children) => getSlotElementData(children, newClasses))
        .map(element => isObject(element) ? 
            createEl(
                element.tag,
                element.data,
                element.children
            ) : element
        );
}

export {
    getSlotElementData,
    getCssClasses,
    getSlots,
}